import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import LoadingOverlay from './LoadingOverlay';
import DetailedOrdersModal from './DetailedOrdersModal';

const CustomerInsights = ({ restaurantId }) => {
    const [insights, setInsights] = useState([]);
    const [groupBy, setGroupBy] = useState('today'); // Options: today, yesterday, week, month, overall
    const [orderHistory, setOrderHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDetailedOrders, setSelectedDetailedOrders] = useState([]); // Detailed orders for "View Complete Bill"
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const restaurantTaxPercentage = JSON.parse(localStorage.getItem('restaurantData'))?.taxPercentage || 0;

    // Fetch insights data based on groupBy and custom date filter
    const fetchInsights = async () => {
        try {
            let url = `${API_ENDPOINTS.ANALYTICS_CUSTOMER_INSIGHTS}?`;
            setIsLoading(true);
            if (customStartDate && customEndDate) {
                url += `startDate=${customStartDate}&endDate=${customEndDate}&restaurantId=${restaurantId}`;
                const response = await axios.get(url);
                setInsights(response.data);
                setIsLoading(false);
                return
            }
            url += `groupBy=${groupBy}&restaurantId=${restaurantId}`;

            console.log(url, "URL")
            const response = await axios.get(url);
            setInsights(response.data);
            setIsLoading(false);
        } catch (err) {
            setError('Failed to load customer insights');
            setIsLoading(false);
        }
    };


    useEffect(() => {
        fetchInsights();
    }, [groupBy, customStartDate, customEndDate]);

    console.log(insights,"insights")
    // Summary metrics with null checks
    const totalOrders = insights.reduce((acc, insight) => acc + (insight.orderCount || 0), 0);
    const totalSpent = insights.reduce((acc, insight) => acc + (insight.totalSpent || 0), 0);
    const finalAmount = totalSpent + insights.reduce((acc, insight) => acc + (insight.totalSpent * (insight.restaurantTaxPercentage / 100) || 0), 0);
    const serviceAmountTotal = insights.reduce((acc, insight) => acc + (insight.totalServiceAmount || 0), 0);
    const overallAvgOrderValue = totalOrders ? totalSpent / totalOrders : 0;
    const newCustomersCount = insights.filter(insight => (insight.orderCount || 0) === 1).length;
    const repeatCustomersCount = insights.filter(insight => (insight.orderCount || 0) > 1).length;
    // Total tax collected: if backend returns totalTax in each insight, sum them; otherwise assume percentage of totalSpent.
    const totalTaxCollected = insights.reduce(
        (acc, insight) => acc + (insight.totalTax || (insight.totalSpent || 0) * (insight.restaurantTaxPercentage / 100)),
        0
    );

    const overallEarnings = finalAmount + serviceAmountTotal;

    // Fetch detailed orders for "View Complete Bill" action
    const viewCompleteBill = async (insight) => {
        try {
            setIsLoading(true);
            
            // If we have orderIds in the insight, use them to fetch specific orders
            // This ensures we only get the orders for this specific insight group
            if (insight.orderIds && insight.orderIds.length > 0) {
                // Join orderIds with comma for the API
                const orderIdsParam = insight.orderIds.join(',');
                const params = { 
                    orderIds: orderIdsParam
                };
                
                const response = await axios.get(API_ENDPOINTS.ANALYTICS_ORDERS_BY_PERIOD, { params });
                setSelectedDetailedOrders(response.data);
            } else {
                // Fallback: use period and mobileNumber (backward compatibility)
                const params = { 
                    period: insight._id.period, 
                    groupBy 
                };
                
                // Only add mobileNumber if it exists and is not empty or "Guest"
                // Use originalMobileNumber if available, otherwise check _id.mobileNumber
                // For orders without mobileNumber, _id.mobileNumber will have "ORDER_" prefix
                const mobileNum = insight.originalMobileNumber || (insight._id.mobileNumber && !insight._id.mobileNumber.startsWith('ORDER_') ? insight._id.mobileNumber : null);
                if (mobileNum && mobileNum.trim() !== '' && mobileNum !== 'Guest') {
                    params.mobileNumber = mobileNum;
                }
                
                const response = await axios.get(API_ENDPOINTS.ANALYTICS_ORDERS_BY_PERIOD, { params });
                setSelectedDetailedOrders(response.data);
            }
            
            setIsLoading(false);
        } catch (err) {
            setError("Failed to load detailed orders");
            console.error(err);
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && <LoadingOverlay />}
            <div className="space-y-3">
                {/* Filters */}
                <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Customer insights</p>
                        <p className="text-[11px] text-stone-400">
                            See high‑value, new, and repeat customers over time.
                        </p>
                    </div>
                    <div className="flex-1" />
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="space-y-0.5">
                            <label className="block text-[10px] font-medium text-stone-600">Group by</label>
                            <select
                                value={groupBy}
                                onChange={(e) => setGroupBy(e.target.value)}
                                className="border border-stone-300 rounded-lg px-2.5 py-1.5 text-[11px] bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                            >
                                <option value="today">Today</option>
                                <option value="yesterday">Yesterday</option>
                                <option value="week">This week</option>
                                <option value="month">This month</option>
                                <option value="overall">Overall</option>
                            </select>
                        </div>
                        <div className="flex items-end gap-1.5">
                            <div className="space-y-0.5">
                                <label className="block text-[10px] font-medium text-stone-600">Start</label>
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    className="border border-stone-300 rounded-lg px-2.5 py-1.5 text-[11px] bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                                />
                            </div>
                            <div className="space-y-0.5">
                                <label className="block text-[10px] font-medium text-stone-600">End</label>
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    className="border border-stone-300 rounded-lg px-2.5 py-1.5 text-[11px] bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                                />
                            </div>
                            <button
                                onClick={() => fetchInsights()}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600"
                            >
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>

                {/* Summary metrics */}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 p-4 sm:p-5 shadow-xs">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800 mb-1 flex items-center gap-1.5">
                            <span>💰</span>
                            <span>Total revenue</span>
                        </p>
                        <p className="text-sm sm:text-base text-emerald-900 font-bold">₹{overallEarnings.toFixed(2)}</p>
                        <p className="text-[11px] text-emerald-700/80 mt-0.5">Base + tax + service</p>
                    </div>
                    <div className="rounded-xl border border-sky-100 bg-sky-50/80 p-4 sm:p-5 shadow-xs">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-800 mb-1 flex items-center gap-1.5">
                            <span>📦</span>
                            <span>Base revenue</span>
                        </p>
                        <p className="text-sm sm:text-base text-sky-900 font-bold">₹{totalSpent.toFixed(2)}</p>
                        <p className="text-[11px] text-sky-700/80 mt-0.5">Before tax & service</p>
                    </div>
                    <div className="rounded-xl border border-amber-100 bg-amber-50/80 p-4 sm:p-5 shadow-xs">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800 mb-1 flex items-center gap-1.5">
                            <span>📊</span>
                            <span>Avg order value</span>
                        </p>
                        <p className="text-sm sm:text-base text-amber-900 font-bold">₹{overallAvgOrderValue.toFixed(2)}</p>
                        <p className="text-[11px] text-amber-700/80 mt-0.5">For selected period</p>
                    </div>
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50/80 p-4 sm:p-5 shadow-xs">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-800 mb-1 flex items-center gap-1.5">
                            <span>🧑‍🤝‍🧑</span>
                            <span>Orders / customers</span>
                        </p>
                        <p className="text-sm sm:text-base text-indigo-900 font-bold">
                            {totalOrders} orders
                        </p>
                        <p className="text-[11px] text-indigo-700/80 mt-0.5">
                            New: {newCustomersCount} · Repeat: {repeatCustomersCount}
                        </p>
                    </div>
                    <div className="rounded-xl border border-rose-100 bg-rose-50/80 p-4 sm:p-5 shadow-xs">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-800 mb-1 flex items-center gap-1.5">
                            <span>🏛️</span>
                            <span>Tax collected</span>
                        </p>
                        <p className="text-sm sm:text-base text-rose-900 font-bold">₹{totalTaxCollected.toFixed(2)}</p>
                        <p className="text-[11px] text-rose-700/80 mt-0.5">Rate: {restaurantTaxPercentage}%</p>
                    </div>
                    <div className="rounded-xl border border-lime-100 bg-lime-50/80 p-4 sm:p-5 shadow-xs">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-lime-800 mb-1 flex items-center gap-1.5">
                            <span>⚡</span>
                            <span>Platform fees</span>
                        </p>
                        <p className="text-sm sm:text-base text-lime-900 font-bold">₹{serviceAmountTotal.toFixed(2)}</p>
                        <p className="text-[11px] text-lime-700/80 mt-0.5">All service amounts</p>
                    </div>
                    <div className="rounded-xl border border-fuchsia-100 bg-fuchsia-50/80 p-4 sm:p-5 shadow-xs">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-fuchsia-800 mb-1 flex items-center gap-1.5">
                            <span>🌱</span>
                            <span>New vs repeat</span>
                        </p>
                        <p className="text-sm sm:text-base text-fuchsia-900 font-bold">
                            {newCustomersCount} new · {repeatCustomersCount} repeat
                        </p>
                        <p className="text-[11px] text-fuchsia-700/80 mt-0.5">Customer mix</p>
                    </div>
                    <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-4 sm:p-5 shadow-xs">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-800 mb-1 flex items-center gap-1.5">
                            <span>🧾</span>
                            <span>Avg orders / customer</span>
                        </p>
                        <p className="text-sm sm:text-base text-slate-900 font-bold">
                            {insights.length ? (totalOrders / insights.length).toFixed(2) : '0.00'}
                        </p>
                        <p className="text-[11px] text-slate-700/80 mt-0.5">Loyalty indicator</p>
                    </div>
                </div>

                {error && <div className="text-xs text-red-500">{error}</div>}

                {/* Table */}
                <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
                    <div className="px-3 py-2 border-b border-stone-200 bg-stone-50/80">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">
                            Customer insight rows
                        </p>
                    </div>
                    <div className="overflow-x-auto max-h-[540px] overflow-y-auto">
                        <table className="min-w-full text-xs">
                            <thead className="bg-stone-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Period
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Customer
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Mobile
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Table
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Offer
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Message
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Orders
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Spent (₹)
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Tax ({restaurantTaxPercentage}%)
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Service (₹)
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Offer discount (₹)
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Final total (₹)
                                    </th>
                                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-stone-600 uppercase tracking-wider border-b border-stone-200">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-stone-100">
                                {insights.map((insight, idx) => {
                                    const finalTotal = insight.finalTotal || ((insight.totalServiceAmount && insight.totalServiceAmount > 0 ? insight.totalServiceAmount : 0) + insight.totalSpent + (insight.totalTax || insight.totalSpent * (insight.restaurantTaxPercentage / 100)) - (insight.totalOfferDiscount ? insight.totalOfferDiscount : 0));
                                    return (
                                        <tr key={idx} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {insight._id.period}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="font-medium text-gray-900">{insight.customerName}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-gray-900 font-mono">
                                                    {insight.originalMobileNumber && insight.originalMobileNumber !== 'Guest' 
                                                        ? insight.originalMobileNumber 
                                                        : 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                    {insight.tableName || 'Order from Counter'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {insight.offerName ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                        {insight.offerName}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 text-xs">No offer</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-gray-600 max-w-xs truncate" title={insight.message || "-"}>
                                                    {insight.message || "-"}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    {insight.orderCount}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-gray-900 font-semibold">₹{insight.totalSpent.toFixed(2)}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-gray-900 font-semibold">₹{(insight.totalTax || (insight.totalSpent * (insight.restaurantTaxPercentage / 100))).toFixed(2)}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-gray-900 font-semibold">₹{insight.totalServiceAmount > 0 ? insight.totalServiceAmount.toFixed(2) : "0.00"}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-green-700 font-bold text-lg">₹{insight.totalOfferDiscount ? insight.totalOfferDiscount.toFixed(2) : "0.00"}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-green-700 font-bold text-lg">₹{finalTotal.toFixed(2)}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <button
                                                    onClick={() => viewCompleteBill(insight)}
                                                    className="inline-flex items-center gap-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                                                >
                                                    👁️ View Bill
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    
                    {insights.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-gray-400 text-6xl mb-4">📊</div>
                            <p className="text-gray-500 text-lg">No customer insights data available</p>
                        </div>
                    )}
                </div>
                {selectedDetailedOrders && selectedDetailedOrders.length > 0 && (
                    <DetailedOrdersModal
                        orders={selectedDetailedOrders}
                        onClose={() => setSelectedDetailedOrders([])}
                    />
                )}
            </div>
        </>
    );
};

export default CustomerInsights;
